import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import Select from 'react-select';
import { debounce } from 'lodash';
import { RotatingLines } from "react-loader-spinner";
import "./CourseMaster.css";

export default function CourseSubdetails() {
  // State management
  const [state, setState] = useState({
    mcourses: [],
    courses: [],
    loading: true,
    formSubmitting: false,
    formData: {
      courseSubdetailsId: 0,
      courseId: 0,
      courseName: "",
      courseTopicName: "",
      courseTopicDescription: "",
      courseSubDetailStatus: true,
      courseSubDetailsPeriority: 0,
    },
    searchQuery: "",
    filterStatus: "all",
    filterCourse: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  useEffect(() => {
    console.log("state ============ ", state);
  }, [state])

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchMainCourses();
        await fetchCourses();
      } catch (error) {
        console.error("Error in initial data loading:", error);
      }
    };
    
    fetchData();
  }, []);
  
  // API calls
  const fetchMainCourses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Course/SuperAdminCourseMasterList`, {
        headers: getHeaders(),
      });
      updateState({ mcourses: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching main courses:", error);
      Swal.fire("Error!", "Failed to load main courses.", "error");
      throw error;
    }
  };
  
  const fetchCourses = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}Course/CourseSubDetailsList`, {
        headers: getHeaders(),
      });
      updateState({
        courses: Array.isArray(response.data) ? response.data : [],
        loading: false
      });
    } catch (error) {
      console.error("Error fetching course subdetails:", error);
      Swal.fire("Error!", "Failed to load course subdetails.", "error");
      updateState({ loading: false });
      throw error;
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value
      }
    });
  };

  const handleSelectChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        courseId: selectedOption ? selectedOption.value : 0,
        courseName: selectedOption ? selectedOption.label : ""
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      [e.target.name]: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        courseSubdetailsId: 0,
        courseId: 0,
        courseName: "",
        courseTopicName: "",
        courseTopicDescription: "",
        courseSubDetailStatus: true,
        courseSubDetailsPeriority: 0,
      }
    });
  };

  const handleEdit = (course) => {
    updateState({
      formData: {
        ...course,
        courseSubDetailStatus: course.courseSubDetailStatus !== false
      }
    });
  };

  const handleDelete = async (courseSubdetailsId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}Course/DeleteCourseSubDetails`,
        { courseSubDetailsId: courseSubdetailsId },
        { headers: getHeaders() }
      );
      updateState({
        courses: state.courses.filter(item => item.courseSubdetailsId !== courseSubdetailsId)
      });
      Swal.fire("Deleted!", "Course subdetail has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting course subdetail:", error);
      Swal.fire("Error", "Failed to delete course subdetail", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}Course/CourseSubDetailsAddEdit`,
        state.formData,
        { headers: getHeaders() }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text: response.data.response === "SUCCESS"
          ? "Course subdetail saved successfully"
          : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchCourses();
        document.getElementById("closeCourseSubdetailModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving course subdetail:", error);
      Swal.fire("Error", "Failed to save course subdetail", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  // Filter and pagination
  const filteredCourses = state.courses.filter(item => {
    const matchesSearch =
      item.courseName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      item.courseTopicName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      item.courseTopicDescription.toLowerCase().includes(state.searchQuery.toLowerCase());

    const matchesStatus =
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.courseSubDetailStatus) ||
      (state.filterStatus === "inactive" && !item.courseSubDetailStatus);

    const matchesCourse =
      state.filterCourse === "all" ||
      item.courseId.toString() === state.filterCourse;

    return matchesSearch && matchesStatus && matchesCourse;
  });

  const pageCount = Math.ceil(filteredCourses.length / state.itemsPerPage);
  const displayedCourses = filteredCourses.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  // Get course name for display
  const getCourseName = (courseId) => {
    const course = state.mcourses.find(c => c.courseId === courseId);
    return course ? course.courseName : "N/A";
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">Course Subdetails Management</h4>

                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Name, Topic or Description"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>

                      {/* Filters */}
                      <div className="d-flex align-items-center gap-2">
                        {/* Course Filter */}
                        <div className="d-flex align-items-center">
                          <i className="fas fa-book me-2 text-muted"></i>
                          <select
                            className="form-select"
                            name="filterCourse"
                            value={state.filterCourse}
                            onChange={handleFilterChange}
                            style={{ minWidth: '120px' }}
                          >
                            <option value="all">All Courses</option>
                            {state.mcourses.map(course => (
                              <option key={course.courseId} value={course.courseId}>
                                {course.courseName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Status Filter */}
                        {/* <div className="d-flex align-items-center">
                          <i className="fas fa-filter me-2 text-muted"></i>
                          <select
                            className="form-select"
                            name="filterStatus"
                            value={state.filterStatus}
                            onChange={handleFilterChange}
                            style={{ minWidth: '120px' }}
                          >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div> */}
                      </div>

                      {/* Add Course Subdetail Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#courseSubdetailModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Subdetail</span>
                      </button>
                    </div>
                  </div>

                  {/* Course Subdetails Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <RotatingLines color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading course subdetails...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped course-subdetails-table">
                          <thead className="table-primary">
                            <tr>
                              <th className="col-sub-id">ID</th>
                              <th className="col-sub-course">Course</th>
                              <th className="col-sub-topic">Topic</th>
                              <th className="col-sub-description">Description</th>
                              <th className="col-sub-priority">Priority</th>
                              <th className="col-sub-actions text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedCourses.length > 0 ? (
                              displayedCourses.map((item) => (
                                <tr key={item.courseSubdetailsId}>
                                  <td className="col-sub-id">{item.courseSubdetailsId}</td>
                                  <td className="col-sub-course">{getCourseName(item.courseId)}</td>
                                  <td className="col-sub-topic">
                                    <div className="text-truncate-subdetails" title={item.courseTopicName}>
                                      {item.courseTopicName}
                                    </div>
                                  </td>
                                  <td className="col-sub-description">
                                    <div className="text-truncate-subdetails" title={item.courseTopicDescription}>
                                      {item.courseTopicDescription}
                                    </div>
                                  </td>
                                  <td className="col-sub-priority">{item.courseSubDetailsPeriority}</td>
                                  <td className="col-sub-actions text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#courseSubdetailModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.courseSubdetailsId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No subdetails"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No course subdetails found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#courseSubdetailModal"
                                      onClick={resetForm}
                                    >
                                      Add New Subdetail
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedCourses.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Subdetail Modal */}
      <div className="modal fade" id="courseSubdetailModal" tabIndex="-1" aria-labelledby="courseSubdetailModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="courseSubdetailModalLabel">
                {state.formData.courseSubdetailsId > 0 ? 'Edit Course Subdetail' : 'Add New Course Subdetail'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row decrease_gap">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="courseId" className="form-label required">Course</label>
                    <Select
                      id="courseId"
                      options={state.mcourses.map(course => ({
                        value: course.courseId,
                        label: `${course.courseName}`
                      }))}
                      value={state.mcourses.find(course => course.courseId === state.formData.courseId) ? {
                        value: state.formData.courseId,
                        label: state.mcourses.find(course => course.courseId === state.formData.courseId).courseName
                      } : null}
                      onChange={handleSelectChange}
                      placeholder="Select Course"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="courseTopicName" className="form-label required">Topic Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="courseTopicName"
                      name="courseTopicName"
                      value={state.formData.courseTopicName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="courseTopicDescription" className="form-label">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      id="courseTopicDescription"
                      name="courseTopicDescription"
                      value={state.formData.courseTopicDescription}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="courseSubDetailsPeriority" className="form-label">Priority</label>
                    <input
                      type="number"
                      className="form-control no-spinners"
                      id="courseSubDetailsPeriority"
                      name="courseSubDetailsPeriority"
                      value={state.formData.courseSubDetailsPeriority}
                      onChange={handleInputChange}
                    />
                  </div>
                {/* {state.formData.courseSubdetailsId > 0 && (
                  <div className="col-md-6 mb-3">
                    <label htmlFor="courseSubDetailStatus" className="form-label">Status</label>
                    <select
                      className="form-select"
                      id="courseSubDetailStatus"
                      name="courseSubDetailStatus"
                      value={state.formData.courseSubDetailStatus}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                )} */}
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button type="button" id="closeCourseSubdetailModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}